<template>
  <div class="container-fluid sort-wrapper">
    <h1 class="text-center sort-title">Sort Challenge</h1>
    <p class="text-center sort-info">
      Click the numbers in order from <strong>smallest to largest</strong>.
      <span v-if="difficulty === 'Expert'" class="expert-note"> Expert includes negatives &amp; decimals.</span>
    </p>

    <!-- Difficulty selector -->
    <div class="text-center difficulty-bar">
      <button
        v-for="d in difficulties"
        :key="d.label"
        class="btn btn-sm diff-btn"
        :class="difficulty === d.label ? 'btn-active' : 'btn-outline'"
        @click="setDifficulty(d)"
      >
        {{ d.label }}
      </button>
    </div>

    <!-- Feedback message -->
    <transition name="fade">
      <div v-if="feedback" class="text-center feedback" :class="feedbackClass">
        {{ feedback }}
      </div>
    </transition>

    <!-- Number tiles -->
    <div class="tiles-grid">
      <transition-group name="pop">
        <div
          v-for="tile in tiles"
          :key="tile.id"
          class="tile"
          :class="tileClass(tile)"
          @click="pickTile(tile)"
        >
          {{ tile.value }}
        </div>
      </transition-group>
    </div>

    <!-- Next round button (shown when round is complete) -->
    <div class="text-center" style="margin-top: 24px;" v-if="roundComplete">
      <button class="btn next-btn" @click="newRound">Next Round</button>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";

const DIFFICULTIES = [
  { label: "Easy",   count: 6,  min: 1,    max: 20,  negatives: false, decimals: false },
  { label: "Medium", count: 10, min: 1,    max: 50,  negatives: false, decimals: false },
  { label: "Hard",   count: 15, min: 1,    max: 100, negatives: false, decimals: false },
  { label: "Expert", count: 20, min: -999, max: 999, negatives: true,  decimals: true  },
];

let tileIdCounter = 0;

export default {
  data() {
    return {
      difficulties: DIFFICULTIES,
      difficulty: "Easy",
      count: 6,
      min: 1,
      max: 20,
      negatives: false,
      decimals: false,
      tiles: [],
      selected: [],   // ids in the order the user clicked
      score: 0,
      round: 1,
      feedback: "",
      feedbackClass: "",
      roundComplete: false,
    };
  },
  computed: {
    sortedValues() {
      return [...this.tiles].sort((a, b) => a.value - b.value).map(t => t.value);
    },
  },
  methods: {
    setDifficulty(d) {
      this.difficulty = d.label;
      this.count = d.count;
      this.min = d.min;
      this.max = d.max;
      this.negatives = d.negatives;
      this.decimals = d.decimals;
      this.score = 0;
      this.round = 1;
      this.newRound();
    },

    generateTiles() {
      const values = new Set();

      if (this.decimals || this.negatives) {
        // Expert mode: mix of negatives, decimals, and large positives
        const range = this.max - this.min; // e.g. 1998 for -999..999
        let attempts = 0;
        while (values.size < this.count && attempts < 2000) {
          attempts++;
          // Raw random in [min, max]
          let raw = Math.random() * range + this.min;

          // ~40% chance of decimal (1 decimal place)
          if (this.decimals && Math.random() < 0.4) {
            raw = Math.round(raw * 10) / 10;
          } else {
            raw = Math.round(raw);
          }

          // Avoid 0 so the set stays interesting
          if (raw === 0) continue;

          const key = raw.toString();
          if (!values.has(key)) values.add(key);
        }
        const arr = [...values].map(v => parseFloat(v));
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr.map(v => ({ id: ++tileIdCounter, value: v, state: "idle" }));
      }

      // Standard integer mode (Easy / Medium / Hard)
      while (values.size < this.count) {
        values.add(Math.floor(Math.random() * (this.max - this.min + 1)) + this.min);
      }
      const arr = [...values];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr.map(v => ({ id: ++tileIdCounter, value: v, state: "idle" }));
    },

    newRound() {
      this.tiles = this.generateTiles();
      this.selected = [];
      this.feedback = "";
      this.feedbackClass = "";
      this.roundComplete = false;
    },

    pickTile(tile) {
      if (tile.state !== "idle" || this.roundComplete) return;

      const nextExpected = this.sortedValues[this.selected.length];

      if (tile.value === nextExpected) {
        tile.state = "correct";
        this.selected.push(tile.id);
        this.showFeedback("✓ Correct!", "feedback-correct");

        if (this.selected.length === this.tiles.length) {
          // Round complete
          this.score = Math.max(0, this.score - 6);
          this.roundComplete = true;
          this.round += 1;
          swal("Round complete!", {
            closeOnClickOutside: false,
          });
        }
      } else {
        // Wrong pick - mark it, deduct points, reset after short delay
        tile.state = "wrong";
        if (this.score > 0) this.score = Math.max(0, this.score - 1);
        this.showFeedback("✗ Wrong! Try again.", "feedback-wrong");

        setTimeout(() => {
          tile.state = "idle";
        }, 700);
      }
    },

    tileClass(tile) {
      return {
        "tile-idle":    tile.state === "idle",
        "tile-correct": tile.state === "correct",
        "tile-wrong":   tile.state === "wrong",
      };
    },

    showFeedback(msg, cls) {
      this.feedback = msg;
      this.feedbackClass = cls;
      clearTimeout(this._feedbackTimer);
      this._feedbackTimer = setTimeout(() => {
        this.feedback = "";
      }, 1200);
    },
  },
  created() {
    this.newRound();
  },
};
</script>

<style scoped>
.sort-wrapper {
  padding: 20px 16px 60px;
  min-height: 100vh;
}

.sort-title {
  margin-bottom: 6px;
  color: rgb(3, 53, 48);
}

.sort-info {
  font-size: 17px;
  margin-bottom: 16px;
}

.expert-note {
  display: block;
  font-size: 14px;
  color: rgb(3, 53, 48);
  opacity: 0.75;
  margin-top: 4px;
}

/* Difficulty buttons */
.difficulty-bar {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.diff-btn {
  border-radius: 20px;
  padding: 4px 18px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-active {
  background-color: rgb(3, 53, 48);
  color: white;
  border: none;
}

.btn-outline {
  background-color: transparent;
  color: rgb(3, 53, 48);
  border: 1px solid rgb(3, 53, 48);
}

.btn-outline:hover {
  background-color: rgb(169, 247, 234);
}

/* Stats bar */
.stats-bar {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.stat-pill {
  background: rgb(169, 247, 234);
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 15px;
  color: rgb(3, 53, 48);
}

/* Feedback */
.feedback {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  height: 28px;
}

.feedback-correct { color: rgb(3, 120, 60); }
.feedback-wrong   { color: rgb(180, 30, 30); }

/* Tiles grid */
.tiles-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin-top: 10px;
}

.tile {
  width: 70px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.15s, background-color 0.2s;
  padding: 0 4px;
}

.tile-idle {
  background-color: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 2px solid rgb(3, 53, 48);
}

.tile-idle:hover {
  transform: scale(1.08);
  background-color: rgb(100, 230, 210);
}

.tile-correct {
  background-color: rgb(3, 53, 48);
  color: white;
  border: 2px solid rgb(3, 53, 48);
  cursor: default;
  transform: scale(1.05);
}

.tile-wrong {
  background-color: rgb(220, 50, 50);
  color: white;
  border: 2px solid rgb(180, 20, 20);
  animation: shake 0.3s ease;
}

/* Next round button */
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 32px;
  border-radius: 6px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}

.next-btn:hover {
  background-color: rgb(5, 80, 70);
}

/* Animations */
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25%       { transform: translateX(-6px); }
  75%       { transform: translateX(6px); }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.4s; }
.fade-enter, .fade-leave-to            { opacity: 0; }

.pop-enter-active { animation: pop-in 0.3s ease-out forwards; }
@keyframes pop-in {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
</style>
